import { SEND_REQUIRES_ADDRESS, SETUP_EFFORT_ORDER } from 'constants/home';
import { OnboardingChecklistResponse, OnboardingTask, OnboardingTaskKey } from 'types/interfaces/onboarding';

// The Setup Banner's state machine (Asana 1218957945501621, Decision 14 and the
// Setup Banner States block). Pure: no React, no dates. Every rule is unit-tested.
//
// Ruling 2026-09-28: VERIFY_PHONE auto-credits on the backend, so after verify it
// arrives COMPLETED with pointsAwarded true and the chip is `claimed`; state A
// goes straight to B. The rules below stay generic on purpose: on a backend
// without the auto-credit the same task would surface as `ready` (state C),
// which is a safety net, not a state anyone designs copy for.

export type ChipState = 'ready' | 'inProgress' | 'open' | 'waiting' | 'locked' | 'claimed';

/** The featured-task states of the spec. 'gone' = every reward claimed, the slot is removed. */
export type BannerState = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'gone';

export const CHIP_ORDER: ChipState[] = ['ready', 'inProgress', 'open', 'waiting', 'locked', 'claimed'];

export const WANT_TARGETS: Partial<Record<OnboardingTaskKey, number>> = {
  ADD_WANT_1: 1,
  ADD_WANT_10: 10,
  ADD_WANT_50: 50,
};

export const SOCIAL_KEYS: OnboardingTaskKey[] = ['FOLLOW_TWITTER', 'FOLLOW_BLUESKY'];

/** Tasks the shipping address unlocks when the backend requires one to send. */
export const ADDRESS_GATED_KEYS: OnboardingTaskKey[] = ['SEND_FIRST_TRADE', 'COMPLETE_FIRST_TRADE'];

export type ChipStates = Partial<Record<OnboardingTaskKey, ChipState>>;

const byKey = (tasks: OnboardingTask[]): Map<OnboardingTaskKey, OnboardingTask> => new Map(tasks.map((t) => [t.taskKey, t]));

/**
 * The user's live phone flag from the Home payload (pass 7, 2026-09-29). The
 * server gates every claim on User.phoneVerified, which the checklist carries
 * as prerequisiteCompleted; the VERIFY_PHONE completion row can be stale
 * (verified once, phone removed since), so it is never the source.
 */
export const checklistPhoneVerified = (
  checklist: Pick<OnboardingChecklistResponse, 'phoneVerified' | 'prerequisiteCompleted'> | undefined
): boolean => checklist?.phoneVerified ?? checklist?.prerequisiteCompleted ?? false;

/**
 * The claim endpoint's phone gate: OnboardingController maps it to
 * 400 { status, message } with "Phone verification is required ..."; a `code`
 * is honoured if the backend adds one.
 */
export const isPhoneGateError = (error: unknown): boolean => {
  const data = (error as { data?: { code?: unknown; message?: unknown } } | null | undefined)?.data;
  if (data?.code === 'PHONE_VERIFICATION_REQUIRED') return true;
  return typeof data?.message === 'string' && /phone verification/i.test(data.message);
};

export const isClaimed = (task: OnboardingTask | undefined): boolean => !!task && task.status === 'COMPLETED' && task.pointsAwarded;
export const isReady = (task: OnboardingTask | undefined): boolean => !!task && task.status === 'COMPLETED' && !task.pointsAwarded;

export const deriveChipStates = (
  tasks: OnboardingTask[],
  wantListCount: number,
  sendRequiresAddress: boolean = SEND_REQUIRES_ADDRESS,
  phoneVerified: boolean = true
): ChipStates => {
  const map = byKey(tasks);
  const addressDone = map.get('ADD_ADDRESS')?.status === 'COMPLETED';
  const sendDone = map.get('SEND_FIRST_TRADE')?.status === 'COMPLETED';
  const states: ChipStates = {};
  tasks.forEach((task) => {
    const key = task.taskKey;
    if (isClaimed(task)) {
      states[key] = 'claimed';
      return;
    }
    if (key === 'VERIFY_PHONE' && task.status === 'COMPLETED') {
      // Verified: mobile auto-credits on verify (ruling 2026-09-28), so a
      // COMPLETED row is claimed whatever pointsAwarded says and never enters
      // Claim all. Unverified: the row is stale (verified once, phone removed
      // since); the chip is open again and never ready (pass 7).
      states[key] = phoneVerified ? 'claimed' : 'open';
      return;
    }
    if (isReady(task)) {
      states[key] = 'ready';
      return;
    }
    if (task.status === 'LOCKED') {
      states[key] = 'locked';
      return;
    }
    // AVAILABLE from here on
    if (key === 'COMPLETE_FIRST_TRADE' && sendDone) {
      states[key] = 'waiting';
      return;
    }
    if (sendRequiresAddress && !addressDone && ADDRESS_GATED_KEYS.includes(key)) {
      states[key] = 'locked';
      return;
    }
    const target = WANT_TARGETS[key];
    if (target && target > 1 && wantListCount >= 1 && wantListCount < target) {
      states[key] = 'inProgress';
      return;
    }
    states[key] = 'open';
  });
  return states;
};

export interface FeaturedPick {
  state: BannerState;
  /** The task on the left; null for the Claim-all form of C, for G, and for 'gone'. */
  taskKey: OnboardingTaskKey | null;
  /** Every COMPLETED task with pointsAwarded false, in chip order. */
  readyKeys: OnboardingTaskKey[];
  /** The featured task is the user's own pick (tag YOUR PICK, "Back to suggestion" shown). */
  picked: boolean;
  /** State A only: the phone was verified before (a COMPLETED VERIFY_PHONE row) but is not now. */
  reverify: boolean;
}

const keysIn = (chips: ChipStates, state: ChipState): OnboardingTaskKey[] =>
  (Object.keys(chips) as OnboardingTaskKey[]).filter((key) => chips[key] === state);

/**
 * Decision 14, first match wins: 1 anything ready, 2 a want threshold already
 * started, 3 the address when it gates the trade tasks, 4 the quickest open task,
 * 5 never a waiting task. States A, I, G and H are the shapes those rules take
 * when the open set is a single prerequisite, only socials, nothing at all, or
 * one last reward.
 */
export const pickFeatured = (
  tasks: OnboardingTask[],
  chips: ChipStates,
  wantListCount: number,
  pickedKey: OnboardingTaskKey | null = null,
  phoneVerified: boolean = true
): FeaturedPick => {
  const readyKeys = sortChipKeys(tasks, chips).filter((key) => chips[key] === 'ready');
  const open = keysIn(chips, 'open');
  const inProgress = keysIn(chips, 'inProgress');
  const waiting = keysIn(chips, 'waiting');
  const claimedCount = keysIn(chips, 'claimed').length;

  if (tasks.length > 0 && claimedCount === tasks.length) {
    return { state: 'gone', taskKey: null, readyKeys, picked: false, reverify: false };
  }

  // The server refuses every other claim until the phone is verified (pass 7,
  // 2026-09-29): the gate outranks the user's pick and rule 1, so nothing ready
  // is ever offered. The ready set is still reported for the totals.
  if (!phoneVerified) {
    const reverify = tasks.some((t) => t.taskKey === 'VERIFY_PHONE' && t.status === 'COMPLETED');
    return { state: 'A', taskKey: 'VERIFY_PHONE', readyKeys, picked: false, reverify };
  }

  // The user's own pick holds while that chip is still actionable
  if (pickedKey && (chips[pickedKey] === 'open' || chips[pickedKey] === 'inProgress')) {
    return { state: 'B', taskKey: pickedKey, readyKeys, picked: true, reverify: false };
  }

  // Rule 1: a reward sitting unclaimed always wins
  if (readyKeys.length > 0) {
    const everythingElseClaimed = readyKeys.length === 1 && claimedCount === tasks.length - 1;
    return { state: everythingElseClaimed ? 'H' : 'C', taskKey: readyKeys.length === 1 ? readyKeys[0] : null, readyKeys, picked: false, reverify: false };
  }

  // Day one: the prerequisite is the only thing that can be open
  if (chips.VERIFY_PHONE === 'open') {
    return { state: 'A', taskKey: 'VERIFY_PHONE', readyKeys, picked: false, reverify: false };
  }

  // Rule 2: momentum beats a fresh start; the nearest threshold first
  if (inProgress.length > 0) {
    const next = (['ADD_WANT_10', 'ADD_WANT_50'] as OnboardingTaskKey[]).find((key) => inProgress.includes(key)) ?? inProgress[0];
    return { state: 'D', taskKey: next, readyKeys, picked: false, reverify: false };
  }

  // Only socials left, nothing waiting on anyone: the optional state
  if (open.length > 0 && open.every((key) => SOCIAL_KEYS.includes(key)) && waiting.length === 0) {
    return { state: 'I', taskKey: SETUP_EFFORT_ORDER.find((key) => open.includes(key)) ?? open[0], readyKeys, picked: false, reverify: false };
  }

  // Rule 4: the quickest open task. Rule 3 (a task that unlocks others) is the
  // address, which sits in the effort order after email, the socials and the
  // first want: state B (email featured with the address still open, mockup 6f)
  // and state E (the address featured once those are done) both follow from
  // the one order. When the address comes up and it gates the trade tasks, the
  // banner takes the E shape.
  const quickest = SETUP_EFFORT_ORDER.find((key) => open.includes(key)) ?? open[0];
  if (quickest === 'ADD_ADDRESS' && SEND_REQUIRES_ADDRESS) {
    return { state: 'E', taskKey: 'ADD_ADDRESS', readyKeys, picked: false, reverify: false };
  }
  if (quickest) {
    return { state: waiting.length > 0 ? 'F' : 'B', taskKey: quickest, readyKeys, picked: false, reverify: false };
  }

  // Rule 5: never a waiting task; when it is all that is left, say so
  if (waiting.length > 0) {
    return { state: 'G', taskKey: waiting[0], readyKeys, picked: false, reverify: false };
  }

  // Nothing open, nothing ready, nothing waiting, not all claimed: only locked
  // tasks remain. Cannot happen with the real dependency graph; render nothing.
  return { state: 'gone', taskKey: null, readyKeys, picked: false, reverify: false };
};

/** Chip sort: ready, in progress, open, waiting, locked, claimed; displayOrder inside each group. */
export const sortChipKeys = (tasks: OnboardingTask[], chips: ChipStates): OnboardingTaskKey[] =>
  [...tasks]
    .sort((a, b) => {
      const ra = CHIP_ORDER.indexOf(chips[a.taskKey] ?? 'open');
      const rb = CHIP_ORDER.indexOf(chips[b.taskKey] ?? 'open');
      return ra - rb || a.displayOrder - b.displayOrder;
    })
    .map((t) => t.taskKey);

export interface SetupTotals {
  totalPossible: number;
  claimedPoints: number;
  readyPoints: number;
  /** totalPossible minus claimed: unclaimed plus unfinished, the affordability budget */
  remainingPoints: number;
  /** Points on tasks not finished yet: the big "Still waiting for you" figure (review 2026-09-28) */
  unfinishedPoints: number;
  claimedCount: number;
  /** COMPLETED tasks, claimed or not: the "{n} of 10 done" figure */
  doneCount: number;
  unclaimedCount: number;
  /** Tasks not finished yet: the "across {n} tasks" figure */
  unfinishedCount: number;
}

export const setupTotals = (tasks: OnboardingTask[], chips: ChipStates): SetupTotals => {
  let totalPossible = 0;
  let claimedPoints = 0;
  let readyPoints = 0;
  let claimedCount = 0;
  let doneCount = 0;
  tasks.forEach((task) => {
    totalPossible += task.pointsReward;
    const state = chips[task.taskKey];
    if (state === 'claimed') {
      claimedPoints += task.pointsReward;
      claimedCount += 1;
    }
    if (state === 'ready') readyPoints += task.pointsReward;
    if (task.status === 'COMPLETED') doneCount += 1;
  });
  return {
    totalPossible,
    claimedPoints,
    readyPoints,
    remainingPoints: totalPossible - claimedPoints,
    unfinishedPoints: totalPossible - claimedPoints - readyPoints,
    claimedCount,
    doneCount,
    unclaimedCount: tasks.length - claimedCount,
    unfinishedCount: tasks.length - doneCount,
  };
};

export interface BarSegment {
  key: OnboardingTaskKey;
  weight: number;
  state: ChipState;
  featured: boolean;
}

/** One segment per task in display order, sized by its reward; the featured one gets the ring. */
export const barSegments = (tasks: OnboardingTask[], chips: ChipStates, featuredKey: OnboardingTaskKey | null): BarSegment[] =>
  [...tasks]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((task) => ({
      key: task.taskKey,
      weight: task.pointsReward,
      state: chips[task.taskKey] ?? 'open',
      featured: task.taskKey === featuredKey,
    }));
